// AnalysisPanel — the country indicators view of the Analysis tab (US-879 ·
// R-1/R-5/R-7). Fills the PanelHost.AnalysisView slot: reads the selected
// country from UIContext (single source of truth — map click and header
// dropdown both land here, R-5), fetches its scorecard through
// useCountryData, and renders whatever config/analysisConfig.js declares —
// US-880/881 extend that inventory, not this component.
//
// ADB degradation (R-7): a missing payload section renders as "—" (scorecard)
// or skips the chart — no value is ever invented. Border-exception economies
// (India/Pakistan/Nepal/Bhutan/Hong Kong) get an info note: the map keeps
// showing regional, unclipped layers per ADB cartographic rules while the
// indicators below stay national.
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import useUI from "../../hooks/useUI";
import useCountryData from "../../hooks/useCountryData";
import { CHARTS, DETAIL_SECTIONS, SCORECARDS } from "../../config/analysisConfig";
import { BORDER_EXCEPTION_ISO3 } from "../../config/selectionConfig";
import { ViewHeader } from "../panel/PanelHost";
import IndicatorScorecard from "./IndicatorScorecard";
import BandBreakdown from "./BandBreakdown";
import MatrixTable from "./MatrixTable";
import BarIndicator from "./charts/BarIndicator";
import DonutIndicator from "./charts/DonutIndicator";

const CHART_COMPONENTS = { bar: BarIndicator, donut: DonutIndicator };

// Detail-section types (US-917 · R-5): the config declares { type, build },
// this map picks the component. US-880/881 add types/entries, not panel code.
const SECTION_COMPONENTS = { bands: BandBreakdown, matrix: MatrixTable };

const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 0.75,
};

// No selection yet — the Analysis tab's empty state (R-3): a prompt, not an
// error. Selecting on the map or in the header dropdown fills the panel.
const SelectCountryPrompt = () => (
  <Box sx={{ px: 2, py: 2 }}>
    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
      Select a country on the map or from the header dropdown to see its
      connectivity indicators.
    </Typography>
  </Box>
);

// Loading state (R-3, US-917 R-7): skeletons echo the loaded layout
// (scorecard grid + chart blocks + one block per detail section) so the
// panel doesn't jump when data lands.
const LoadingSkeleton = () => (
  <Box sx={{ px: 2, py: 2 }} data-testid="analysis-loading" aria-busy="true">
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
      {SCORECARDS.map(({ id }) => (
        <Skeleton key={id} variant="rounded" height={76} />
      ))}
    </Box>
    <Skeleton variant="rounded" height={180} sx={{ mt: 2.5 }} />
    <Skeleton variant="rounded" height={120} sx={{ mt: 2.5 }} />
    {DETAIL_SECTIONS.map(({ id }) => (
      <Skeleton key={id} variant="rounded" height={96} sx={{ mt: 1.5 }} />
    ))}
  </Box>
);

// Error state (R-3): the ApiError surfaces as a message, never a crash. The
// analyst can retry by re-selecting; nothing here invents data (R-7).
const LoadError = ({ error }) => (
  <Alert severity="error" sx={{ mx: 2, mt: 2, fontSize: 13 }}>
    Couldn't load this country's indicators
    {error?.detail ? ` — ${error.detail}` : ""}. Try selecting the country
    again.
  </Alert>
);

// The loaded content: scorecard grid + every chart the config can build from
// this payload. A chart whose section is absent is skipped (R-7).
const CountryIndicators = ({ data }) => (
  <Box sx={{ px: 2, py: 2 }}>
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
      {SCORECARDS.map(({ id, label, caption, getDisplay }) => (
        <IndicatorScorecard
          key={id}
          label={label}
          value={getDisplay(data)}
          caption={caption}
        />
      ))}
    </Box>
    {CHARTS.map((chart) => {
      const props = chart.build(data);
      if (!props) return null;
      const Chart = CHART_COMPONENTS[chart.type];
      return (
        <Box key={chart.id} sx={{ mt: 2.5 }}>
          <Typography component="h3" sx={SECTION_LABEL_SX}>
            {chart.title}
          </Typography>
          <Chart {...props} />
        </Box>
      );
    })}
    {/* US-917 detail sections: one Accordion each, default expanded like the
        legacy scorecard. A section whose payload is absent is omitted (R-4). */}
    <Box sx={{ mt: 2.5 }}>
      {DETAIL_SECTIONS.map((section) => {
        const props = section.build(data);
        if (!props) return null;
        const Section = SECTION_COMPONENTS[section.type];
        return (
          <Accordion
            key={section.id}
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              "&:before": { display: "none" },
              "&:not(:first-of-type)": { mt: 1.5 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Typography component="h3" sx={{ ...SECTION_LABEL_SX, mb: 0 }}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Section {...props} />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  </Box>
);

const AnalysisPanel = () => {
  const { selectedCountry } = useUI();
  const iso3 = selectedCountry?.iso3 || null;
  const { data, loading, error } = useCountryData(iso3);

  if (!iso3) {
    return (
      <>
        <ViewHeader eyebrow="Regional Overview" title="Asia-Pacific" />
        <SelectCountryPrompt />
      </>
    );
  }

  return (
    <>
      <ViewHeader
        eyebrow="Country Analysis"
        title={selectedCountry.name}
        subtitle="Connectivity indicators and investment estimates."
      />
      {BORDER_EXCEPTION_ISO3.has(iso3) && (
        <Alert severity="info" sx={{ mx: 2, mt: 2, fontSize: 13 }}>
          Per ADB cartographic rules, map layers for this economy stay regional
          (not clipped to its boundary). The indicators below are national.
        </Alert>
      )}
      {loading && <LoadingSkeleton />}
      {!loading && error && <LoadError error={error} />}
      {!loading && !error && data && <CountryIndicators data={data} />}
    </>
  );
};

export default AnalysisPanel;
